import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scheduleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Calendar, Filter, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  rescheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  missed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const formatStatus = (status) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export default function FollowUpsList() {
  const navigate = useNavigate();
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadFollowUps();
  }, [page, filters]);

  const loadFollowUps = async () => {
    try {
      setLoading(true);
      const params = {
        event_type: 'follow_up',
        status: filters.status !== 'all' ? filters.status : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      };

      const response = await scheduleAPI.getCalendarEvents(params);
      let allFollowups = response.data.filter(event => event.event_type === 'follow_up');

      // Client-side search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        allFollowups = allFollowups.filter(followup =>
          followup.title?.toLowerCase().includes(searchLower) ||
          followup.description?.toLowerCase().includes(searchLower) ||
          followup.lead_name?.toLowerCase().includes(searchLower)
        );
      }

      // Sort by updated_at or created_at (newest first)
      allFollowups.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });

      // Pagination
      const total = Math.ceil(allFollowups.length / itemsPerPage);
      setTotalPages(total);
      const startIdx = (page - 1) * itemsPerPage;
      const paginatedFollowups = allFollowups.slice(startIdx, startIdx + itemsPerPage);

      setFollowups(paginatedFollowups);
    } catch (error) {
      console.error('Failed to load follow-ups:', error);
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      start_date: '',
      end_date: '',
    });
    setPage(1);
  };

  const handleStatusChange = async (followup, newStatus) => {
    try {
      await scheduleAPI.updateEvent(followup.id, { status: newStatus });
      toast.success(`Status updated to ${formatStatus(newStatus)}`);
      loadFollowUps();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6" data-testid="followups-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Follow-ups</h1>
          <p className="text-muted-foreground mt-1">
            View and manage all follow-ups with leads
          </p>
        </div>
        <Button onClick={() => navigate('/schedule')}>
          <Calendar className="h-4 w-4 mr-2" />
          View Calendar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              placeholder="Search follow-ups..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            
            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rescheduled">Rescheduled</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="Start Date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />

            <Input
              type="date"
              placeholder="End Date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Follow-ups Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading follow-ups...</div>
          ) : followups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No follow-ups found
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-muted-foreground">
                <strong>Note:</strong> To update follow-up status, click on the lead name to open lead details.
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead</TableHead>
                    <TableHead>Follow-up Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {followups.map((followup) => (
                    <TableRow key={followup.id}>
                      <TableCell className="font-medium">
                        {followup.related_to ? (
                          <button
                            onClick={() => navigate(`/leads/${followup.related_to}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                          >
                            {followup.lead_name}
                          </button>
                        ) : (
                          <span>{followup.lead_name}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatLocal(followup.start_time, 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${statusColors[followup.status]} inline-flex items-center justify-center min-w-[100px]`}>
                          {formatStatus(followup.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {followup.lead_phone || '-'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {followup.description || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
